// The tv panel's chrome: each section folds under its heading, the lists trade height by the grip between them, and the
// panel's width follows its left edge. The layout is the viewer's, kept in storage as `basho.panel` and carried on the
// html element (boot.ts puts it there before the first paint): `data-fold` names the folded sections, `--w-<section>`
// are the lists' shares of the height (as pixels, the last time they were set), `--panel-w` the width. Being on the
// html element and not the panel, it outlives the channel change's swap of the panel's markup (page.ts swapPanel).
// Every listener is on the document, added once: they survive the swaps and the site's navigation.
import { PANEL_KEY } from "./env.ts";
import { readPref, writePref } from "./sound/prefs.ts";

const KEY = PANEL_KEY;
/** the least a list is dragged down to, px: its heading and a row */
export const MIN_SECTION = 72;
/** the panel's narrowest, px, and its widest as a share of the window */
export const MIN_WIDTH = 280,
  MAX_WIDTH_SHARE = 0.5;
/** an arrow key's step on a grip, px */
const STEP = 24;

/** the viewer's layout */
/** the viewer's layout; `hidden`: the whole panel put away, the stage the full width (Sam, 2026-10-17: "collapse the info pane to just see the coolness") */
type Layout = { fold: string[]; sizes: Record<string, number>; width?: number; hidden?: boolean };

/** the stored layout, or the default (nothing folded, the server's shares, the default width) */
export function readLayout(): Layout {
  const out: Layout = { fold: [], sizes: {} };
  try {
    const v = JSON.parse(readPref(KEY) || "null");
    if (!v || typeof v !== "object") return out;
    if (Array.isArray(v.fold)) out.fold = v.fold.filter((s: unknown) => typeof s === "string");
    if (v.sizes && typeof v.sizes === "object")
      for (const [k, n] of Object.entries(v.sizes)) if (typeof n === "number" && n > 0) out.sizes[k] = n;
    if (typeof v.width === "number") out.width = v.width;
    if (v.hidden === true) out.hidden = true;
  } catch (_) {
    /* a bad value: the defaults */
  }
  return out;
}

/** put the layout on the html element (as boot.ts does) and keep it */
export function setLayout(l: Layout, save = true) {
  const h = document.documentElement;
  if (l.fold.length) h.dataset.fold = l.fold.join(" ");
  else delete h.dataset.fold;
  for (const p of Array.from(h.style)) if (p.startsWith("--w-")) h.style.removeProperty(p);
  const sized = Object.keys(l.sizes);
  for (const k of sized) h.style.setProperty(`--w-${k}`, String(Math.round(l.sizes[k])));
  if (sized.length) h.dataset.sized = "1";
  else delete h.dataset.sized;
  if (l.width !== undefined) h.style.setProperty("--panel-w", `${Math.round(l.width)}px`);
  else h.style.removeProperty("--panel-w");
  if (l.hidden) h.dataset.panelHidden = "1";
  else delete h.dataset.panelHidden;
  if (save)
    writePref(
      KEY,
      JSON.stringify({
        fold: l.fold,
        sizes: Object.fromEntries(sized.map((k) => [k, Math.round(l.sizes[k])])),
        ...(l.width !== undefined ? { width: Math.round(l.width) } : {}),
        ...(l.hidden ? { hidden: true } : {}),
      }),
    );
  syncPanel();
}

const nameOf = (sec: Element) => (sec as HTMLElement).dataset.section || "";
const folded = (sec: Element) => (document.documentElement.dataset.fold || "").split(" ").includes(nameOf(sec));

/** the headings' `aria-expanded` true to the fold, the sheet's tabs to the section showing, the stake rows to their
 * opening: after the first paint and after every swap of the panel */
export function syncPanel(root: ParentNode = document) {
  const shown = !document.documentElement.dataset.panelHidden;
  for (const i of document.querySelectorAll<HTMLInputElement>("input[data-panel-show]")) i.checked = shown;
  for (const b of root.querySelectorAll<HTMLElement>(".side-panel [data-sec-toggle]")) {
    const sec = b.closest("[data-section]");
    if (sec) b.setAttribute("aria-expanded", folded(sec) ? "false" : "true");
  }
  const sheet = document.documentElement.dataset.sheet || "";
  for (const t of root.querySelectorAll<HTMLElement>(".side-panel [data-sheet-tab]"))
    t.setAttribute("aria-selected", t.dataset.sheetTab === sheet ? "true" : "false");
  for (const b of root.querySelectorAll<HTMLElement>(".side-panel [data-stake-toggle]"))
    b.setAttribute("aria-expanded", b.closest("li")?.classList.contains("open") ? "true" : "false");
}

/**
 * The sheet (under 860px, responsive.css): the tab's section slides up over the ring; the same tab again, or null,
 * puts it away. On the html element like the folds, so a channel change keeps it open. Not stored: every visit starts
 * with the ring clear.
 */
export function showSheet(name: string | null) {
  const h = document.documentElement;
  if (name && h.dataset.sheet !== name) h.dataset.sheet = name;
  else delete h.dataset.sheet;
  syncPanel();
}

/** open one pairing's stake form under it, closing any other; the same row again closes it */
export function openStake(li: Element) {
  const was = li.classList.contains("open");
  for (const o of document.querySelectorAll(".side-panel li.pairing.open")) o.classList.remove("open");
  if (!was) {
    li.classList.add("open");
    const amount = li.querySelector<HTMLInputElement>("input[name=amount]");
    if (amount) {
      amount.focus({ preventScroll: true });
      try {
        amount.select();
      } catch (_) {
        /* jsdom */
      }
    }
  }
  syncPanel();
}

/** fold a section, or open it */
export function toggleSection(name: string) {
  const l = readLayout(),
    i = l.fold.indexOf(name);
  if (i >= 0) l.fold.splice(i, 1);
  else l.fold.push(name);
  setLayout(l);
}

/** the open list sections, in order */
const openLists = (panel: Element) =>
  Array.from(panel.querySelectorAll(":scope > section.grow[data-section]")).filter((s) => !folded(s)) as HTMLElement[];

/**
 * A grip moved by `dy` px from where it was taken: the section under it and the open list above it trade that height,
 * neither under MIN_SECTION. Every open list's share is set from the heights they had when taken (`start`), so nothing
 * else moves. Returns the shares to store, or null when there is no open list above to trade with.
 */
export function dragSizes(
  start: Map<string, number>,
  above: string,
  below: string,
  dy: number,
): Record<string, number> | null {
  const a = start.get(above),
    b = start.get(below);
  if (a === undefined || b === undefined) return null;
  const total = a + b,
    na = Math.min(total - MIN_SECTION, Math.max(MIN_SECTION, a + dy));
  const out: Record<string, number> = Object.fromEntries(start);
  out[above] = na;
  out[below] = total - na;
  return out;
}

/** the width the panel takes when its edge is dragged `dx` px from where it was taken (leftwards widens) */
export const dragWidth = (start: number, dx: number, win: number) =>
  Math.min(win * MAX_WIDTH_SHARE, Math.max(MIN_WIDTH, start - dx));

/** a grip's two sections: its own and the open list above, with every open list's height as it stands */
function gripPair(grip: HTMLElement) {
  const below = grip.closest<HTMLElement>("section[data-section]"),
    panel = below?.parentElement;
  if (!below || !panel) return null;
  const lists = openLists(panel),
    i = lists.indexOf(below);
  if (i <= 0) return null;
  const start = new Map(lists.map((s) => [nameOf(s), s.getBoundingClientRect().height]));
  return { above: nameOf(lists[i - 1]), below: nameOf(below), start };
}

function dragging(on: boolean) {
  document.documentElement.classList.toggle("panel-dragging", on);
}

/** follow a pointer from `e` until it lets go, calling `move` with how far it has come */
function follow(e: PointerEvent, grip: HTMLElement, move: (dx: number, dy: number) => void, done: () => void) {
  const x0 = e.clientX,
    y0 = e.clientY;
  e.preventDefault();
  try {
    grip.setPointerCapture(e.pointerId);
  } catch (_) {
    /* jsdom */
  }
  dragging(true);
  const onMove = (m: PointerEvent) => move(m.clientX - x0, m.clientY - y0);
  const onUp = () => {
    grip.removeEventListener("pointermove", onMove);
    grip.removeEventListener("pointerup", onUp);
    grip.removeEventListener("pointercancel", onUp);
    dragging(false);
    done();
  };
  grip.addEventListener("pointermove", onMove);
  grip.addEventListener("pointerup", onUp);
  grip.addEventListener("pointercancel", onUp);
}

let wired = false;
/** the listeners, once for the life of the page */
export function wirePanelUi() {
  if (wired) return;
  wired = true;
  // the panel put away or brought back: a tap on the stage, the gear's switch, or the backslash key (not while typing)
  const hide = () => {
    const l = readLayout();
    l.hidden = !l.hidden;
    setLayout(l);
  };
  document.addEventListener("change", (e) => {
    const i = e.target instanceof HTMLInputElement && e.target.matches("[data-panel-show]") ? e.target : null;
    if (i && i.checked === !!readLayout().hidden) hide();
  });
  document.addEventListener("keydown", (e) => {
    const t = e.target instanceof HTMLElement ? e.target : null;
    if (
      e.key !== "\\" ||
      e.ctrlKey ||
      e.metaKey ||
      e.altKey ||
      t?.closest("input, textarea, select, [contenteditable]")
    )
      return;
    if (!document.querySelector(".home")) return;
    e.preventDefault();
    hide();
  });
  document.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    // one tap on the stage puts the panel away or brings it back (Sam, 2026-10-17: "one tap panel go away no button"):
    // anywhere in the ring but a link, a control, or a tap that ends a selection of the words
    if (
      t.closest(".home .ring") &&
      !t.closest("a, button, input, select, textarea, label, summary, [role=button], [data-sec-toggle]") &&
      !String(window.getSelection?.() ?? "") &&
      window.matchMedia("(min-width: 861px)").matches &&
      !document.documentElement.dataset.photo && // photo mode's drags turn the camera (fx/photo.ts)
      !document.body.classList.contains("broadcast")
    ) {
      hide();
      return;
    }
    const b = t.closest<HTMLElement>(".side-panel [data-sec-toggle]"),
      sec = b?.closest("[data-section]");
    if (sec) {
      toggleSection(nameOf(sec));
      return;
    }
    const tab = t.closest<HTMLElement>(".side-panel [data-sheet-tab]");
    if (tab) {
      showSheet(tab.dataset.sheetTab || null);
      return;
    }
    const pair = t.closest(".side-panel [data-stake-toggle]")?.closest("li.pairing");
    if (pair) {
      openStake(pair);
      return;
    }
    // a tap on the ring puts the sheet away
    if (document.documentElement.dataset.sheet && !t.closest(".side-panel")) showSheet(null);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && document.documentElement.dataset.sheet) showSheet(null);
  });
  document.addEventListener("pointerdown", (e) => {
    if (e.button !== 0 || !(e.target instanceof Element)) return;
    const grip = e.target.closest<HTMLElement>(".side-panel .sec-grip, .side-panel [data-panel-grip]");
    if (!grip) return;
    const l = readLayout();
    if (grip.hasAttribute("data-panel-grip")) {
      const start = grip.closest(".side-panel")?.getBoundingClientRect().width || 0;
      follow(
        e,
        grip,
        (dx) => {
          l.width = dragWidth(start, dx, window.innerWidth);
          setLayout(l, false);
        },
        () => setLayout(l),
      );
      return;
    }
    const pair = gripPair(grip);
    if (!pair) return;
    follow(
      e,
      grip,
      (_dx, dy) => {
        const s = dragSizes(pair.start, pair.above, pair.below, dy);
        if (s) {
          l.sizes = { ...l.sizes, ...s };
          setLayout(l, false);
        }
      },
      () => setLayout(l),
    );
  });
  document.addEventListener("dblclick", (e) => {
    const grip =
      e.target instanceof Element
        ? e.target.closest<HTMLElement>(".side-panel .sec-grip, .side-panel [data-panel-grip]")
        : null;
    if (!grip) return;
    const l = readLayout();
    if (grip.hasAttribute("data-panel-grip")) delete l.width;
    else l.sizes = {};
    setLayout(l);
  });
  document.addEventListener("keydown", (e) => {
    const grip =
      e.target instanceof Element
        ? e.target.closest<HTMLElement>(".side-panel .sec-grip, .side-panel [data-panel-grip]")
        : null;
    if (!grip) return;
    const l = readLayout();
    if (grip.hasAttribute("data-panel-grip")) {
      const d = e.key === "ArrowLeft" ? -STEP : e.key === "ArrowRight" ? STEP : 0;
      if (!d) return;
      e.preventDefault();
      l.width = dragWidth(grip.closest(".side-panel")?.getBoundingClientRect().width || 0, d, window.innerWidth);
      setLayout(l);
      return;
    }
    const d = e.key === "ArrowUp" ? -STEP : e.key === "ArrowDown" ? STEP : 0;
    const pair = d ? gripPair(grip) : null;
    if (!pair) return;
    e.preventDefault();
    const s = dragSizes(pair.start, pair.above, pair.below, d);
    if (s) {
      l.sizes = { ...l.sizes, ...s };
      setLayout(l);
    }
  });
}
